// src/encounter/services/use-non-prescriber-plan.ts
// [PROD-2949] Read and save the CALLER's own non-prescriber plan for an encounter (Scrips.Patient):
//   GET /api/Patients/{patientId}/Encounters/{encounterId}/NonPrescriberPlans/mine → plan, or 204 (none yet)
//   PUT /api/Patients/{patientId}/Encounters/{encounterId}/NonPrescriberPlans/mine → the saved plan
// "mine" is decided by the server from the token: the app never names an author, so it can never write
// another clinician's plan. The server also decides whether this persona may write this framework.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/shared/api/api-client';
import type { NonPrescriberPlanDto, SaveNonPrescriberPlanRequest } from '@/encounter/lib/non-prescriber-plan-record';
import { useNoteWrite } from './note-session';

export function nonPrescriberPlanKey(patientId: string | null | undefined, encounterId: string | null | undefined) {
  return ['non-prescriber-plan', 'mine', patientId, encounterId] as const;
}

function minePath(patientId: string, encounterId: string): string {
  return `/api/Patients/${encodeURIComponent(patientId)}/Encounters/${encodeURIComponent(encounterId)}/NonPrescriberPlans/mine`;
}

/** The caller's own plan (null when none yet). Enabled only with both ids AND `enabled`. */
export function useMyNonPrescriberPlan(
  patientId: string | null | undefined,
  encounterId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery<NonPrescriberPlanDto | null>({
    queryKey: nonPrescriberPlanKey(patientId, encounterId),
    enabled: enabled && !!patientId && !!encounterId,
    queryFn: async ({ signal }) => {
      if (!patientId || !encounterId) return null;
      // 204 (no plan yet) parses to undefined.
      return (await get<NonPrescriberPlanDto | undefined>(minePath(patientId, encounterId), { signal })) ?? null;
    },
  });
}

/** Save the caller's own plan; the response replaces the cached read (no refetch needed). */
export function useSaveMyNonPrescriberPlan(patientId: string | null | undefined, encounterId: string | null | undefined) {
  const noteWrite = useNoteWrite();
  const qc = useQueryClient();
  return useMutation<NonPrescriberPlanDto, Error, SaveNonPrescriberPlanRequest>({
    mutationFn: (body) =>
      noteWrite(() => {
        if (!patientId || !encounterId) return Promise.reject(new Error('The encounter is not ready yet.'));
        return put<NonPrescriberPlanDto>(minePath(patientId, encounterId), body);
      }),
    onSuccess: (saved) => {
      qc.setQueryData(nonPrescriberPlanKey(patientId, encounterId), saved);
    },
  });
}
